import React from 'react';
import {
  Mail,
  Phone,
  MessageCircle,
  QrCode,
  Video,
  Globe,
  MessageSquare,
  Copy,
  ExternalLink
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

interface ContactSectionProps {
  title?: string;
  showTitle?: boolean;
  className?: string;
}

export const ContactSection: React.FC<ContactSectionProps> = ({
  title = '联系我们',
  showTitle = true,
  className = ''
}) => {
  const { t } = useLanguage();
  const displayTitle = title === '联系我们' ? t('联系我们', 'Contact Us') : title;
  const contactMethods = [
    {
      icon: <MessageCircle className="w-5 h-5" />,
      title: t('微信公众号', 'WeChat Official Account'),
      value: 'UKVisaAI',
      action: t('复制', 'Copy'),
      actionKey: 'copy'
    },
    {
      icon: <QrCode className="w-5 h-5" />,
      title: t('微信客服', 'WeChat Support'),
      value: 'ukvisa-ai',
      action: t('复制', 'Copy'),
      actionKey: 'copy'
    },
    {
      icon: <Video className="w-5 h-5" />,
      title: 'B站',
      value: '英国签证AI助手',
      action: t('关注', 'Follow'),
      actionKey: 'follow'
    },
    {
      icon: <Globe className="w-5 h-5" />,
      title: '小红书',
      value: 'UKVisaAI',
      action: t('关注', 'Follow'),
      actionKey: 'follow'
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: t('邮箱', 'Email'),
      value: 'service@ukvisa-ai.cn',
      action: t('复制', 'Copy'),
      actionKey: 'copy'
    },
    {
      icon: <Phone className="w-5 h-5" />,
      title: t('客服热线', 'Support Hotline'),
      value: '400-1234-5678',
      action: t('拨打', 'Call'),
      actionKey: 'call'
    }
  ];

  const handleAction = (method: string, value: string) => {
    switch (method) {
      case 'copy':
        navigator.clipboard.writeText(value);
        alert(t(`已复制: ${value}`, `Copied: ${value}`));
        break;
      case 'follow':
        window.open(`https://www.${value.toLowerCase().replace(/\s+/g, '')}.com`, '_blank');
        break;
      case 'call':
        window.location.href = `tel:${value.replace(/-/g, '')}`;
        break;
      default:
        break;
    }
  };

  return (
    <div className={`bg-white dark:!bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:!border-slate-700 p-6 ${className}`}>
      {showTitle && (
        <div className="flex items-center gap-2 mb-6">
          <MessageSquare className="w-5 h-5 text-indigo-600 dark:!text-indigo-300" />
          <h3 className="text-lg font-semibold text-gray-900 dark:!text-white">{displayTitle}</h3>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {contactMethods.map((method, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 bg-gray-50 dark:!bg-slate-950 rounded-lg hover:bg-gray-100 dark:hover:!bg-slate-900 transition-colors border border-gray-200 dark:!border-slate-700"
          >
            <div className="flex items-center gap-3">
              <div className="text-indigo-600 dark:!text-indigo-300">
                {method.icon}
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:!text-slate-300">{method.title}</p>
                <p className="text-sm font-medium text-gray-900 dark:!text-white">{method.value}</p>
              </div>
            </div>
            <button
              onClick={() => handleAction(method.actionKey, method.value)}
              className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 dark:!bg-indigo-500 dark:hover:!bg-indigo-400 transition-colors inline-flex items-center gap-1"
            >
              {method.actionKey === 'copy' ? <Copy className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
              {method.action}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 rounded-lg border bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 dark:!from-slate-950 dark:!via-slate-900 dark:!to-indigo-950 dark:!border-slate-600 dark:!shadow-none">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="rounded-lg p-3 bg-slate-900 border border-slate-700 shadow-sm dark:!bg-slate-950 dark:!border-slate-500 dark:!shadow-none">
            <QrCode className="w-20 h-20 text-white dark:!text-white" />
          </div>
          <div className="text-center sm:text-left">
            <h4 className="font-semibold text-gray-900 dark:!text-white mb-1">{t('扫码添加客服', 'Scan to add support')}</h4>
            <p className="text-sm text-gray-700 dark:!text-slate-200">{t('一对一咨询，获取专属方案', 'One-to-one consultation and tailored plans.')}</p>
            <p className="text-xs text-gray-600 dark:!text-slate-300 mt-2">{t('工作日 9:00-18:00 在线', 'Weekdays 9:00-18:00 online')}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 text-center text-sm text-gray-500 dark:!text-slate-300">
        {t('客服热线: 400-1234-5678 (24小时)', 'Support hotline: 400-1234-5678 (24 hours)')}
      </div>
    </div>
  );
};
